import {
  Box,
  ListItemAvatar,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { useFMContext } from '@pflegenavi/frontend/family-member-context';
import {
  Iconify,
  ListItemIconStyle,
  ListItemStyle,
  MenuPopover,
  NameAvatar,
  TruncatedTooltipText,
} from '@pflegenavi/web-components';
import type { FC } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInitializeFM } from '../../hooks/useInitializeFM';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface SelectResidentProps {}

const ITEM_HEIGHT = 65;

export const SelectResident: FC<SelectResidentProps> = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<HTMLElement | null>(null);
  const theme = useTheme();

  const { initResident, selectedResidentId } = useFMContext();
  const { selectedResident, residents } = useInitializeFM();

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleSelectedResident = (id: string) => {
    initResident(id);
    handleClose();
  };

  const residentsToList = useMemo(() => {
    const residentSelected = residents?.find(
      (resident) => resident.residentId === selectedResidentId
    );
    const otherResidents = residents?.filter(
      (resident) => resident.residentId !== selectedResidentId
    );
    if (residentSelected && otherResidents) {
      return [residentSelected, ...otherResidents];
    }
    return residents;
  }, [residents, selectedResidentId]);

  const SCROLLBAR_HEIGHT = useMemo(() => {
    const defaultHeight = 3 * ITEM_HEIGHT;
    if (residents) {
      const nbrOfItems = residents.length;
      return nbrOfItems > 3 ? defaultHeight : nbrOfItems * ITEM_HEIGHT;
    }
    return defaultHeight;
  }, [residents]);

  if (!residentsToList || !selectedResident) {
    return null;
  }

  return (
    <>
      <ListItemStyle
        activeRoot={true}
        onClick={handleOpen}
        style={{
          backgroundColor: theme.palette.secondary.light,
          marginBottom: 40,
        }}
      >
        <ListItemIconStyle active={true}>
          <Iconify
            icon={'eva:person-fill'}
            width={20}
            height={20}
            sx={{ minWidth: 20 }}
          />
        </ListItemIconStyle>
        <TruncatedTooltipText
          text={`${selectedResident.firstName} ${selectedResident.lastName}`}
          maxWidth={200}
          sx={{ color: 'primary.main', fontWeight: 600 }}
        />
      </ListItemStyle>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        arrow="top-left"
        sx={{
          mt: 0.5,
          ml: 6,
          width: 320,
          boxShadow: 'rgba(0, 0, 0, 0.15) 0px 5px 15px',
          pb: 2,
          paddingX: 1.2,
          '& .MuiMenuItem-root': {
            px: 1.5,
            height: ITEM_HEIGHT,
            borderRadius: 1.5,
          },
        }}
      >
        <Typography variant="h6" sx={{ p: 1.5 }}>
          {t('fm-web-navigation.resident-selection')}
        </Typography>

        <Box style={{ overflowY: 'auto' }} sx={{ height: SCROLLBAR_HEIGHT }}>
          {residentsToList.map((resident) => (
            <MenuItem
              key={resident.residentId}
              onClick={() => handleSelectedResident(resident.residentId)}
              sx={{
                bgcolor:
                  selectedResidentId === resident.residentId ? 'grey.200' : '',
              }}
            >
              <ListItemAvatar sx={{ position: 'relative' }}>
                <NameAvatar
                  name={`${resident.firstName} ${resident.lastName}`}
                  gender={resident.gender}
                />
              </ListItemAvatar>

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <TruncatedTooltipText
                  text={`${resident.firstName} ${resident.lastName}`}
                  maxWidth={220}
                  sx={{
                    typography: 'body1',
                    color: 'primary.main',
                  }}
                />
                {selectedResidentId === resident.residentId && (
                  <TruncatedTooltipText
                    text={t('common.current-selection')}
                    maxWidth={220}
                    sx={{
                      typography: 'caption',
                      color: 'text.secondary',
                    }}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Box>
      </MenuPopover>
    </>
  );
};
